import { useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { isKnownRoute } from '@/lib/knownRoutes';
import { publicAxios } from '@/lib/axios';
import { lookupBrowserSessionToken } from '@/lib/auth/lookupBrowserSessionToken';

export const Sso = () => {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const handoffToken = params.get('token');
    const rawRedirect = params.get('redirect_to') || '/';

    // Only redirect to a same-origin relative path ("/", not "//") that matches
    // a known app route. Anything unsafe or non-existent falls back to "/".
    const isSafeRelative = rawRedirect.startsWith('/') && !rawRedirect.startsWith('//');
    const pathname = rawRedirect.split(/[?#]/)[0];
    const redirectTo = isSafeRelative && isKnownRoute(pathname) ? rawRedirect : '/';

    if (!handoffToken) {
      window.location.replace('/login');
      return;
    }

    let cancelled = false;
    (async () => {
      // The partner deep-links with a short-lived (120s) handoff token. Exchange
      // it for a normal full-length session JWT so the session doesn't silently
      // die after two minutes. If the exchange endpoint is unreachable (e.g. a
      // frontend-before-backend deploy) we fall back to the raw handoff token —
      // no worse than the previous behaviour.
      let exchangedJwt: string | null = null;
      try {
        const res = await publicAxios.post('/api/v1/networking-cortes/sso/exchange', {
          token: handoffToken,
        });
        exchangedJwt = res.data?.jwt ?? null;
      } catch {
        exchangedJwt = null;
      }
      if (cancelled) return;

      const sessionToken = await lookupBrowserSessionToken(exchangedJwt, handoffToken);
      if (cancelled) return;
      if (sessionToken) {
        localStorage.setItem('token', sessionToken);
        window.location.replace(redirectTo);
      } else {
        window.location.replace('/login');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-gray-800 border-gray-700">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <img
              src="/images/autoclipper-logo-full.png"
              alt="Autoclipper Logo"
              className="h-12 w-auto"
            />
          </div>
          <CardTitle className="text-2xl font-bold text-white">Signing you in…</CardTitle>
          <CardDescription className="text-gray-400">
            Please wait while we sign you in.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-400"></div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
